import type { Router } from 'vue-router';
import { api } from '@/lib/api';
import { secureTokenStore } from '@/lib/secure-store';

/**
 * Everything that only exists inside the Capacitor shell.
 *
 * The plugins are imported dynamically so the web bundle never pulls in native shims it will
 * not call. `isNative` is decided by the transport the client was built with — the same
 * `VITE_CLIENT` that selects the mobile token transport — rather than by sniffing the
 * user agent, so the two can never disagree about which build this is.
 */
export const isNative = (): boolean => api.isMobile;

/**
 * Reads the refresh token out of the keychain and hands the client a store that can write
 * back to it.
 *
 * Must complete before the router's first navigation: the guard calls `auth.restore()`, which
 * asks `api.hasSession`, which on native means "is there a refresh token in memory yet".
 * Hydrating late would sign in every returning user as a stranger.
 */
export async function installSecureStore(): Promise<void> {
  const { SecureStorage } = await import('@aparajita/capacitor-secure-storage');

  const store = secureTokenStore(
    {
      get: async (key) => {
        const value = await SecureStorage.get(key);
        // The plugin answers `null` for a missing key, but its type also admits numbers and
        // booleans from other callers' data. Anything that is not a string is not our token.
        return typeof value === 'string' ? value : null;
      },
      set: (key, value) => SecureStorage.set(key, value),
      remove: async (key) => void (await SecureStorage.remove(key)),
    },
    (err, op) => console.error(`[secure-store] ${op} failed`, err),
  );

  await store.hydrate();
  api.attachStore(store);
}

/**
 * Android's hardware back button.
 *
 * Without this the button leaves the app from any screen, discarding a half-filled booking
 * form because the user meant to go back one step. `canGoBack` is Capacitor's view of the
 * *webview* history, which is what `router.back()` unwinds.
 */
export async function installBackButton(router: Router): Promise<void> {
  const { App } = await import('@capacitor/app');

  await App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack && router.currentRoute.value.name !== 'home') router.back();
    else void App.exitApp();
  });
}
